// Draft persistence rules for the feedback compose screen (task-16 brief):
// the typed message survives a failed (or abandoned) submit and is restored
// the next time the screen opens; it is cleared only once a submission
// actually succeeds. Images are never part of this — a Blob cannot survive
// localStorage — which is exactly why this module only ever touches text.
//
// Takes an injectable store instead of importing `localStorage` directly so
// these rules are unit-testable with a plain in-memory fake and no DOM.
// FeedbackCompose.tsx is responsible for handing over `window.localStorage`
// (guarded against throwing in private-browsing contexts — see its own
// `safeLocalStorage` helper) and for calling these at the right moments.
export const FEEDBACK_DRAFT_KEY = 'sorteia:feedbackDraft';

export interface DraftStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** Restore the draft on screen open. Never throws — a storage failure just means no draft. */
export function readFeedbackDraft(store: DraftStore | undefined | null): string {
  if (!store) return '';
  try {
    return store.getItem(FEEDBACK_DRAFT_KEY) || '';
  } catch {
    return '';
  }
}

/**
 * Called on every content change. An empty draft is removed rather than
 * stored as `''` — there is nothing to restore, and it keeps
 * `readFeedbackDraft` and "is there a saved draft" trivially the same check.
 */
export function saveFeedbackDraft(store: DraftStore | undefined | null, content: string): void {
  if (!store) return;
  try {
    if (content) store.setItem(FEEDBACK_DRAFT_KEY, content);
    else store.removeItem(FEEDBACK_DRAFT_KEY);
  } catch {
    // Storage unavailable (private browsing, quota) — the draft simply
    // won't survive a reload, same as if this call never happened.
  }
}

/** Called only after a submit actually succeeds — never on a failed attempt. */
export function clearFeedbackDraft(store: DraftStore | undefined | null): void {
  if (!store) return;
  try {
    store.removeItem(FEEDBACK_DRAFT_KEY);
  } catch {
    // See saveFeedbackDraft.
  }
}
